import { DirectUpload } from "@rails/activestorage";

/**
 * Font Direct Uploader Module
 * Handles direct uploads for preview images and font files
 */

export class FontDirectUploader {
  constructor(cdnHost) {
    this.cdnHost = cdnHost;
  }

  /**
   * Generate unique filename with hash for duplicate handling
   */
  generateFilenameWithHash(originalName) {
    const extension = originalName.split('.').pop().toLowerCase();
    const baseName = originalName.substring(0, originalName.lastIndexOf('.'));

    // Sanitize filename: remove special characters, spaces, and non-ASCII characters
    const safeName = baseName
      .replace(/[^\w\s-]/g, '')    // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '_')         // Replace spaces with underscores
      .replace(/-+/g, '-')          // Replace multiple hyphens with single hyphen
      .replace(/_+/g, '_')          // Replace multiple underscores with single underscore
      .replace(/^[_-]+|[_-]+$/g, '') // Remove leading/trailing underscores or hyphens
      .substring(0, 50);            // Limit filename length

    const hash = Math.random().toString(36).substring(2, 8);
    return `${safeName}_${hash}.${extension}`;
  }

  /**
   * Upload preview image with public ACL
   * Path: space_url/market_font/ext_id/preview_images/filename_hash.ext
   */
  async uploadPreviewImage(file, marketFontExtId) {
    const hashedFilename = this.generateFilenameWithHash(file.name);
    const cdnKey = `market_font/${marketFontExtId}/preview_images/${hashedFilename}`;

    return this.performUpload(file, cdnKey, 'public-read');
  }

  /**
   * Upload font file with private ACL
   * Path: space_url/market_font/ext_id/font_variant/weight/filename.ext
   */
  async uploadFontFile(file, marketFontExtId, variantWeight) {
    // 파일 이름에서 확장자 분리
    const lastDotIndex = file.name.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex > -1 ? file.name.substring(0, lastDotIndex) : file.name;
    const extension = lastDotIndex > -1 ? file.name.substring(lastDotIndex) : '';

    // 타임스탬프를 36진수로 변환하여 짧게 만들기
    const timestamp = Date.now().toString(36);

    // 타임스탬프를 포함한 새로운 파일 이름 생성
    const fileNameWithTimestamp = `${nameWithoutExt}_${timestamp}${extension}`;

    const cdnKey = `market_font/${marketFontExtId}/font_variant/${variantWeight}/${fileNameWithTimestamp}`;

    return this.performUpload(file, cdnKey, 'private');
  }

  /**
   * Perform the actual upload using DirectUpload
   */
  async performUpload(file, cdnKey, acl) {
    // Create FormData with cdn_key parameter
    const formData = new FormData();
    formData.append('cdn_key', cdnKey);

    const upload = new DirectUpload(
      file,
      '/rails/active_storage/direct_uploads',
      {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.setRequestHeader('x-amz-acl', acl);
          // Encode the cdn key to handle non-ASCII characters
          xhr.setRequestHeader('x-amz-meta-cdn-key', encodeURIComponent(cdnKey));
        },
        directUploadWillCreateBlobWithXHR: (xhr) => {
          // Send cdn_key with the blob creation request
          const originalSend = xhr.send;
          xhr.send = function(data) {
            if (data instanceof FormData) {
              data.append('cdn_key', cdnKey);
            } else if (typeof data === 'string') {
              // Parse JSON and add cdn_key
              try {
                const jsonData = JSON.parse(data);
                jsonData.cdn_key = cdnKey;
                data = JSON.stringify(jsonData);
              } catch(e) {
                // Keep original data if not JSON
              }
            }
            originalSend.call(this, data);
          };
        }
      }
    );

    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          reject(error);
        } else {
          resolve({
            cdnKey: cdnKey,  // Use our custom cdn_key
            blobKey: blob.key,
            url: acl === 'public-read' ? `${this.cdnHost}/${cdnKey}` : null
          });
        }
      });
    });
  }

  /**
   * Upload multiple preview images
   */
  async uploadPreviewImages(files, marketFontExtId, progressCallback) {
    const results = [];

    for (let i = 0; i < files.length; i++) {
      try {
        if (progressCallback) {
          progressCallback(i + 1, files.length);
        }

        const result = await this.uploadPreviewImage(files[i], marketFontExtId);
        results.push(result);
      } catch (error) {
        console.error(`Failed to upload preview image ${files[i].name}:`, error);
        throw error;
      }
    }

    return results;
  }

  /**
   * Upload font files for a variant
   */
  async uploadFontFiles(files, marketFontExtId, variantWeight) {
    const results = {};

    for (const file of files) {
      const extension = file.name.split('.').pop().toLowerCase();

      if (!['ttf', 'otf', 'woff', 'woff2'].includes(extension)) {
        throw new Error(`Unsupported font file type: ${extension}`);
      }

      try {
        const result = await this.uploadFontFile(file, marketFontExtId, variantWeight);
        results[extension] = result;
      } catch (error) {
        console.error(`Failed to upload font file ${file.name}:`, error);
        throw error;
      }
    }

    return results;
  }
}

export default FontDirectUploader;